import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
// Import hero image
import heroImage from "../assets/images/hero.jpg"; // Adjust the path as needed

// Styled components
const StyledHeroSection = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: `url(${heroImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundColor: "#000",
  padding: theme.spacing(4, 2),
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(6, 3),
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(8, 4),
    backgroundAttachment: "fixed",
    overflow: "visible",
  },
}));

const StyledOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
});

const StyledContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  textAlign: "center",
  color: "white",
  maxWidth: "1000px",
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    maxHeight: "100vh",
    overflow: "hidden",
  },
}));

const StyledTitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontSize: "1.8rem",
  fontWeight: "bold",
  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.5rem",
    marginBottom: theme.spacing(3),
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "3.5rem",
  },
  [theme.breakpoints.up("lg")]: {
    fontSize: "4.5rem",
  },
}));

const StyledSubtitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontSize: "1.1rem",
  fontWeight: "normal",
  textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.5rem",
    marginBottom: theme.spacing(4),
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2rem",
  },
  [theme.breakpoints.up("lg")]: {
    fontSize: "2.5rem",
  },
}));

const StyledTextContainer = styled(Box)(({ theme }) => ({
  maxWidth: "800px",
  margin: "0 auto",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up("sm")]: {
    marginBottom: theme.spacing(4),
  },
  [theme.breakpoints.down("md")]: {
    flex: 1,
    overflowY: "auto",
    paddingRight: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(255, 255, 255, 0.1)",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255, 255, 255, 0.3)",
      borderRadius: "2px",
      "&:hover": {
        background: "rgba(255, 255, 255, 0.5)",
      },
    },
  },
}));

const StyledHeroText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  lineHeight: 1.6,
  fontSize: "0.9rem",
  opacity: 0.9,
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    marginBottom: theme.spacing(3),
    lineHeight: 1.8,
    fontSize: "1.1rem",
  },
}));

const StyledHeroTextItalic = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  lineHeight: 1.6,
  fontSize: "0.9rem",
  opacity: 0.9,
  textAlign: "center",
  fontStyle: "italic",
  [theme.breakpoints.up("sm")]: {
    marginBottom: theme.spacing(3),
    lineHeight: 1.8,
    fontSize: "1.1rem",
  },
}));

const StyledHeroTagline = styled(Typography)(({ theme }) => ({
  lineHeight: 1.6,
  fontSize: "1rem",
  fontWeight: "bold",
  opacity: 0.95,
  textAlign: "center",
  color: theme.palette.primary.main,
  [theme.breakpoints.up("sm")]: {
    lineHeight: 1.8,
    fontSize: "1.2rem",
  },
}));

const StyledHeroButton = styled(Button)(({ theme }) => ({
  fontSize: "1rem",
  padding: theme.spacing(1, 3),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    padding: theme.spacing(1.5, 4),
  },
}));

const StyledHeaderSection = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexShrink: 0,
  },
}));

interface HeroSectionProps {
  onOpenBookingDialog: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  onOpenBookingDialog,
}) => {
  return (
    <StyledHeroSection
      id="home"
      component="section"
      aria-label="Welkom bij Pippaya"
    >
      {/* Dark overlay */}
      <StyledOverlay aria-hidden="true" />

      {/* Content */}
      <StyledContent>
        <StyledHeaderSection>
          <StyledTitle variant="h1" component="h1">
            Pippaya Hondenopvang
          </StyledTitle>
          <StyledSubtitle variant="h2" component="h2">
            Waar logeren ook een beetje vakantie is
          </StyledSubtitle>
        </StyledHeaderSection>
        
        <StyledTextContainer>
          <StyledHeroText variant="body1">
            Bij Pippaya komt een hond niet zomaar naar de opvang, maar eigenlijk gewoon een beetje <strong>bij ons thuis</strong>.
          </StyledHeroText>

          <StyledHeroText variant="body1">
            Geen kennels en geen grote groepen, maar een kleinschalige opvang waar we elkaar écht leren kennen. We wandelen door de polder, gaan lekker zwemmen als het kan, spelen en snuffelen erop los en ja… soms komen we thuis met meer modder dan hond. 😂
          </StyledHeroText>

          <StyledHeroText variant="body1">
            Daarna lekker afdrogen, een snackje, een knuffel en ergens een fijn plekje zoeken om bij te komen. Want vakantie betekent natuurlijk niet dat je de hele dag druk moet zijn. <strong>Bankhangen hoort er óók bij.</strong> ❤️
          </StyledHeroText>

          <StyledHeroText variant="body1">
            Iedere hond is anders en dat vinden we bij Pippaya juist leuk. Daarom kijken we naar wat een hond nodig heeft om zich hier prettig en thuis te voelen.
          </StyledHeroText>

          <StyledHeroTextItalic variant="body1">
            En terwijl de baasjes genieten van hun vakantie, doen wij hier eigenlijk precies hetzelfde. Alleen met meer honden, meer modderpoten en waarschijnlijk meer snackjes. 😉🐾
          </StyledHeroTextItalic>

          <StyledHeroTagline variant="body1">
            Welkom bij Pippaya — waar de hond misschien stiekem wel de leukste vakantie heeft.
          </StyledHeroTagline>
        </StyledTextContainer>
        
        <StyledHeroButton
          variant="contained"
          color="secondary"
          size="large"
          onClick={onOpenBookingDialog}
          aria-label="Neem contact op met Pippaya"
        >
          Neem Contact Op
        </StyledHeroButton>
      </StyledContent>
    </StyledHeroSection>
  );
};
